import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { DefineNewProjectTablesForm } from '../../../Admin/DataRequest/Popups/DefineNewPorjectPopup/DefineNewProjectTablesForm/DefineNewProjectTablesForm';
import { DataForm } from '../../../../models/DataRequestHub/DataForm';
import { DataTable } from '../../../../models/DataRequestHub/DataTable';
import './SellerDataAndDocumentRequestForm.scss';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Field } from '@availity/form';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import { LinkedDataRequestProject } from '../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { LinkedDataRequestProjectStatus } from '../../../../slices/marketplace/timeline/models/enums/linked-data-request-project-status.enum';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineActionStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { generateGuid } from '../../../../utils/typedHelper';

interface SellerDataAndDocumentRequestFormProps {
    disabled: boolean;
    currentEvent: TimelineEvent;
    onSubmit(
        action: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        dataRequestProject?: LinkedDataRequestProject
    ): void;
    dataRequestProjects: LinkedDataRequestProject[];
    isCreateMode?: boolean;
}
export const SellerDataAndDocumentRequestForm = forwardRef(
    (props: SellerDataAndDocumentRequestFormProps, ref) => {
        const formRef = useRef<any>();
        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));
        const project = props.dataRequestProjects.find(
            (x) => x.id === props.currentEvent.linkedAction?.referenceItemId
        );
        const [selectedDocumentTemplateId, setSelectedDocumentTemplateId] =
            useState<number>(project?.documentTemplateId ?? null);
        const [
            selectedFinancialRequestsTemplateId,
            setSelectedFinancialRequestsTemplateId,
        ] = useState<number>(project?.financialRequestsTemplateId ?? null);
        const [
            selectedQuestionnaireTemplateId,
            setSelectedQuestionnaireTemplateId,
        ] = useState<number>(project?.questionnaireTemplateId ?? null);
        const [selectedDataTableTemplateIds, setSelectedDataTableTemplateIds] =
            useState<number[]>(project?.dataTableTemplateIds ?? []);
        const [
            selectedDataTableViewTemplateIds,
            setSelectedDataTableViewTemplateIds,
        ] = useState<number[]>(project?.dataTableViewTemplateIds ?? []);
        const [questionnaireFormName, setQuestionnaireFormName] = useState('');
        const [questionnaireForms, setQuestionnaireForms] = useState<
            DataForm[]
        >([]);
        const [dataTables, setDataTables] = useState<DataTable[]>([]);
        const [isDocumentDataFormVisible, setIsDocumentDataFormVisible] =
            useState(project ? project.documentTemplateId > 0 : true);
        const [
            isFinancialRequestsDataFormVisible,
            setIsFinancialRequestsDataFormVisible,
        ] = useState(project ? project.financialRequestsTemplateId > 0 : true);
        const [
            isQuestionnaireDataFormVisible,
            setIsQuestionnaireDataFormVisible,
        ] = useState(project ? project.questionnaireTemplateId > 0 : true);
        const [isDataTablesVisible, setIsDataTablesVisible] = useState(
            project ? project.dataTableTemplateIds?.length > 0 : true
        );

        const getTablesByQuestionnaireId = (questionnaireId: number) => {
            const selectedQuestionnaire = questionnaireForms.find(
                (x) => x.id === questionnaireId
            );

            if (!selectedQuestionnaire) {
                return [];
            }
            const tableViewIds = selectedQuestionnaire.questions
                .filter((x) => x.tableViewId)
                .map((x) => x.tableViewId);
            const tableIds = dataTables
                .filter((table) =>
                    table.views.some((view) => tableViewIds.includes(view.id))
                )
                .map((table) => table.id);
            const uniqueTableIds = Array.from(new Set(tableIds));
            return uniqueTableIds;
        };

        const getTableViewsByQuestionnaireId = (questionnaireId: number) => {
            const selectedQuestionnaire = questionnaireForms.find(
                (x) => x.id === questionnaireId
            );

            if (!selectedQuestionnaire) {
                return [];
            }
            const tableViewIds = selectedQuestionnaire.questions
                .filter((x) => x.tableViewId)
                .map((x) => x.tableViewId);
            const uniqueTableViewIds = Array.from(new Set(tableViewIds));
            return uniqueTableViewIds;
        };

        const hasSelectedFormsError = (): boolean => {
            if (
                (isDocumentDataFormVisible &&
                    Number.isNaN(selectedDocumentTemplateId)) ||
                (isFinancialRequestsDataFormVisible &&
                    Number.isNaN(selectedFinancialRequestsTemplateId)) ||
                (isQuestionnaireDataFormVisible &&
                    Number.isNaN(selectedQuestionnaireTemplateId)) ||
                (isDataTablesVisible && !selectedDataTableTemplateIds?.length)
            ) {
                return true;
            }

            return false;
        };

        const isAnyFormSelected = (): boolean =>
            (isDocumentDataFormVisible && !!selectedDocumentTemplateId) ||
            (isFinancialRequestsDataFormVisible &&
                !!selectedFinancialRequestsTemplateId) ||
            (isQuestionnaireDataFormVisible &&
                !!selectedQuestionnaireTemplateId) ||
            (isDataTablesVisible && !!selectedDataTableTemplateIds?.length);

        useEffect(() => {
            formRef.current.setFieldValue(
                'isDaqiFormValid',
                !hasSelectedFormsError() && isAnyFormSelected()
            );
            formRef.current.validateForm();
        }, [
            selectedDataTableTemplateIds,
            selectedDataTableViewTemplateIds,
            selectedDocumentTemplateId,
            selectedFinancialRequestsTemplateId,
            selectedQuestionnaireTemplateId,
            questionnaireFormName,
            isDocumentDataFormVisible,
            isFinancialRequestsDataFormVisible,
            isQuestionnaireDataFormVisible,
            isDataTablesVisible,
        ]);

        useEffect(() => {
            if (project) {
                setSelectedDocumentTemplateId(project.documentTemplateId);
                setSelectedFinancialRequestsTemplateId(
                    project.financialRequestsTemplateId
                );
                setSelectedQuestionnaireTemplateId(
                    project.questionnaireTemplateId
                );
                setSelectedDataTableTemplateIds(project.dataTableTemplateIds);
                setSelectedDataTableViewTemplateIds(
                    project.dataTableViewTemplateIds
                );
                setQuestionnaireFormName(project.questionnaireFormName);
            }
        }, [project]);

        const isActionUpdated = () => {
            if (!project) return true;
            return (
                project.projectName !== formRef.current?.values?.projectName ||
                project.actionLinkText !==
                    formRef.current?.values?.actionLinkText ||
                project.documentTemplateId !== selectedDocumentTemplateId ||
                project.financialRequestsTemplateId !==
                    selectedFinancialRequestsTemplateId ||
                project.questionnaireTemplateId !==
                    selectedQuestionnaireTemplateId ||
                project.questionnaireFormName !== questionnaireFormName ||
                JSON.stringify(project.dataTableTemplateIds ?? []) !==
                    JSON.stringify(selectedDataTableTemplateIds ?? []) ||
                JSON.stringify(project.dataTableViewTemplateIds ?? []) !==
                    JSON.stringify(selectedDataTableViewTemplateIds ?? [])
            );
        };

        return (
            <div className="seller-data-and-document-request-form">
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        projectName: project?.projectName ?? '',
                        actionLinkText: project?.actionLinkText ?? '',
                        isDaqiFormValid:
                            !hasSelectedFormsError() && isAnyFormSelected(),
                    }}
                    validationSchema={yup.object().shape({
                        projectName: yup
                            .string()
                            .required('Project Name is required'),
                        actionLinkText: props.isCreateMode
                            ? yup.string()
                            : yup
                                  .string()
                                  .required('Action Link Text is required'),
                    })}
                    onSubmit={async (values) => {
                        if (values.isDaqiFormValid === false) {
                            return;
                        }
                        if (isActionUpdated()) {
                            const postObj: LinkedDataRequestProject = {
                                id: generateGuid(),
                                projectName: values.projectName,
                                actionLinkText: values.actionLinkText,
                                documentTemplateId: isDocumentDataFormVisible
                                    ? selectedDocumentTemplateId
                                    : 0,
                                financialRequestsTemplateId:
                                    isFinancialRequestsDataFormVisible
                                        ? selectedFinancialRequestsTemplateId
                                        : 0,
                                questionnaireTemplateId:
                                    isQuestionnaireDataFormVisible
                                        ? selectedQuestionnaireTemplateId
                                        : 0,
                                questionnaireFormName:
                                    isQuestionnaireDataFormVisible
                                        ? questionnaireFormName
                                        : '',
                                dataTableTemplateIds: isDataTablesVisible
                                    ? selectedDataTableTemplateIds
                                    : getTablesByQuestionnaireId(
                                          selectedQuestionnaireTemplateId
                                      ),
                                dataTableViewTemplateIds: isDataTablesVisible
                                    ? selectedDataTableViewTemplateIds ?? []
                                    : getTableViewsByQuestionnaireId(
                                          selectedQuestionnaireTemplateId
                                      ) ?? [],
                                status: LinkedDataRequestProjectStatus.Upcoming,
                            };
                            props.onSubmit(
                                {
                                    ...defaultTimelineAction,
                                    actionType: Number(
                                        TimelineActionType.SellerDataAndDocumentRequest
                                    ),
                                    status: TimelineActionStatus.Upcoming,
                                    referenceItemId: postObj.id,
                                },
                                undefined,
                                undefined,
                                postObj
                            );
                        } else {
                            props.onSubmit(
                                {
                                    ...defaultTimelineAction,
                                    actionType: Number(
                                        TimelineActionType.SellerDataAndDocumentRequest
                                    ),
                                    status: TimelineActionStatus.Upcoming,
                                    referenceItemId:
                                        project?.id ?? generateGuid(),
                                },
                                undefined,
                                undefined,
                                project
                            );
                        }
                    }}>
                    {({ errors }) => {
                        return (
                            <>
                                <Field
                                    className="field"
                                    name="projectName"
                                    label="Project Name"
                                    disabled={props.disabled}
                                />

                                {!props.isCreateMode ? (
                                    <Field
                                        className="field"
                                        name="actionLinkText"
                                        label="Action Link Text"
                                        disabled={props.disabled}
                                    />
                                ) : (
                                    []
                                )}

                                <DefineNewProjectTablesForm
                                    isDataTablesVisible={isDataTablesVisible}
                                    setIsDataTablesVisible={
                                        setIsDataTablesVisible
                                    }
                                    selectedDocumentTemplateId={
                                        selectedDocumentTemplateId
                                    }
                                    isDocumentDataFormVisible={
                                        isDocumentDataFormVisible
                                    }
                                    setIsDocumentDataFormVisible={
                                        setIsDocumentDataFormVisible
                                    }
                                    dataTables={dataTables}
                                    selectedDataTableTemplateIds={
                                        selectedDataTableTemplateIds
                                    }
                                    isFinancialRequestsDataFormVisible={
                                        isFinancialRequestsDataFormVisible
                                    }
                                    setIsFinancialRequestsDataFormVisible={
                                        setIsFinancialRequestsDataFormVisible
                                    }
                                    isQuestionnaireDataFormVisible={
                                        isQuestionnaireDataFormVisible
                                    }
                                    setIsQuestionnaireDataFormVisible={
                                        setIsQuestionnaireDataFormVisible
                                    }
                                    setQuestionnaireFormName={
                                        setQuestionnaireFormName
                                    }
                                    selectedDataTableViewTemplateIds={
                                        selectedDataTableViewTemplateIds
                                    }
                                    setSelectedDataTableViewTemplateIds={
                                        setSelectedDataTableViewTemplateIds
                                    }
                                    getTableViewsByQuestionnaireId={
                                        getTableViewsByQuestionnaireId
                                    }
                                    selectedQuestionnaireTemplateId={
                                        selectedQuestionnaireTemplateId
                                    }
                                    getTablesByQuestionnaireId={
                                        getTablesByQuestionnaireId
                                    }
                                    setSelectedDataTableTemplateIds={
                                        setSelectedDataTableTemplateIds
                                    }
                                    setSelectedQuestionnaireTemplateId={
                                        setSelectedQuestionnaireTemplateId
                                    }
                                    setSelectedFinancialRequestsTemplateId={
                                        setSelectedFinancialRequestsTemplateId
                                    }
                                    setSelectedDocumentTemplateId={
                                        setSelectedDocumentTemplateId
                                    }
                                    setQuestionnaireForms={
                                        setQuestionnaireForms
                                    }
                                    setDataTables={setDataTables}
                                    selectedfinafinancialRequestsTemplateId={
                                        selectedFinancialRequestsTemplateId
                                    }
                                    questionnaireFormName={
                                        questionnaireFormName
                                    }
                                    disabled={
                                        props.disabled
                                    }></DefineNewProjectTablesForm>

                                {isActionUpdated() &&
                                props.currentEvent?.linkedAction?.actionType ===
                                    TimelineActionType.SellerDataAndDocumentRequest ? (
                                    <div className="invalid-feedback error-msg">
                                        Saving the Action will delete the
                                        existing project and create a new one.
                                        Also it will delete all PIQ
                                        Questionnaire and Review Document
                                        actions related to the existing project.
                                    </div>
                                ) : (
                                    []
                                )}
                            </>
                        );
                    }}
                </Formik>
            </div>
        );
    }
);
